import React from 'react';
import {classes, style} from './doppe-action-type-spotify-widget.st.css';
import {urlModifyQueryParams} from '@wix/devzai-utils-common';
import {DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeSpotify,
    doppeValidateSpotifyArtistURL,
    doppeValidateSpotifyPlaylistURL,
    SpotifyWidgetSize,
    SpotifyWidgetTheme
} from '../doppe-action-type-spotify';
import {doppeHideableValueIsVisibleAndNotEqualValue} from '../../../client-server-common';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {FlexLayout} from '@wix/devzai-common-client';
import {
    useIsWidgetInFixedHeight
} from '../../../components/doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons';

export const DoppeActionTypeSpotifyWidget = React.memo(function DoppeActionTypeSubscribeEmbedWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeSpotify.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const actionSettings = actionContext.action.settings;

    const {
        url,
        size,
        theme,
        title,
        description,
        ctaButtons
    } = actionSettings

    const embedUrl = transformToEmbedUrl(url);

    const isWidgetInFixedHeight = useIsWidgetInFixedHeight();

    if (!embedUrl) {
        return <div>Invalid URL</div>
    }

    const spotifyUrl = urlModifyQueryParams(embedUrl, {
        theme: theme === SpotifyWidgetTheme.Default ? null : '0',
        utm_source: 'hopp',
    });

    const hastTextContent = doppeHideableValueIsVisibleAndNotEqualValue(title, '') || doppeHideableValueIsVisibleAndNotEqualValue(description, '');
    const hasButton = ctaButtons.filter(button => button.isVisible).length > 0;

    const isWidgetList = doppeValidateSpotifyArtistURL(url) || doppeValidateSpotifyPlaylistURL(url);
    const isActualWidgetInFixedHeight = isWidgetInFixedHeight && isWidgetList;
    const forceMaxHeight = isActualWidgetInFixedHeight && !hasButton && !hastTextContent;;

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {
                hasTextContent: hastTextContent,
                isFixedHeight: isActualWidgetInFixedHeight,
                hasButton: hasButton,
                forceMaxHeight: forceMaxHeight
            }, className)}
            actionContext={actionContext}
            forceMaxHeight={forceMaxHeight}
        >
            <DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
                actionContext={actionContext}
                reportGoalAchieved={(goalType) => {
                    actionContext.reportGoalAchieved({
                        goalType: goalType,
                    })
                }}
                fullWidthMainMedia={false}
                sections={actionSettings.sections}
                buttons={actionSettings.ctaButtons}
                buttonsSettings={{
                    sourceURL: url,
                }}
            >
                <iframe
                    className={style(classes.iframe, {}, forceMaxHeight ? FlexLayout.fillRemainingVerticalSpace : undefined)}
                    src={spotifyUrl}
                    width="100%"
                    height={forceMaxHeight ? undefined : (!isActualWidgetInFixedHeight ? size === SpotifyWidgetSize.Normal ? '352' : '152' : 352)}
                    frameBorder="0"
                    allowFullScreen={undefined}
                    allow="autoplay;
                        clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy"
                >
                </iframe>
                <DoppeActionTypeWidgetInnerTitle>
                    {title}
                </DoppeActionTypeWidgetInnerTitle>
                <DoppeActionTypeWidgetInnerDescription>
                    {description}
                </DoppeActionTypeWidgetInnerDescription>
            </DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons>
        </DoppeActionTypeWidgetLayout>
    );
});

function transformToEmbedUrl(url: string) {
    // Define the regular expression pattern to match the first URL format with query parameters
    const pattern = /^https:\/\/open\.spotify\.com\/([A-Za-z]+)\/([A-Za-z0-9_-]+)(\?.*)?$/;

    // Check if the input URL matches the first format
    const match = url.match(pattern);

    if (match) {
        // Extract the first part (e.g., "playlist") and the {ID} from the matched URL
        const urlType = match[1];
        const id = match[2];

        // Extract the query parameters if they exist
        const queryParams = match[3] || '';

        // Create and return the transformed URL in the second format
        const embedUrl = `https://open.spotify.com/embed/${urlType}/${id}${queryParams}`;
        return embedUrl;
    } else {
        // If the input URL doesn't match the first format, return null to indicate an invalid URL format
        return null;
    }
}
